// One-shot handoff for opening an app's Clark chat with a prompt from a
// different bundle (Fix with Clark from the inbox, or a clone that needs its
// database set up). The producer writes here, then opens the editor; the editor
// consumer reads the entry whose nonce matches `?clarkPrompt=` on this tab.
// localStorage keeps a long prompt out of the URL. Take semantics still clear
// the claimed entry after one successful read.
//
// Keyed per application as a nonce queue, not a single slot: two Fix with Clark
// clicks on the same app, or a clone overlapping a Fix, must not steal each
// other's prompt.
const KEY_PREFIX = "clark_pending_prompt";
const MAX_QUEUE_LENGTH = 20;
const PROMPT_TTL_MS = 10 * 60 * 1000;

function keyFor(applicationId: string): string {
  return `${KEY_PREFIX}:${applicationId}`;
}

interface PendingClarkPrompt {
  applicationId: string;
  // When set, the consumer sends the prompt as a turn instead of only
  // prefilling the composer for the user to review and submit.
  autoSubmit?: boolean;
  nonce: string;
  prompt: string;
}

interface QueuedPrompt {
  autoSubmit?: boolean;
  createdAt: number;
  nonce: string;
  prompt: string;
}

function isQueuedPrompt(value: unknown): value is QueuedPrompt {
  const v = value as Partial<QueuedPrompt> | null;
  return (
    typeof v === "object" &&
    v !== null &&
    typeof v.nonce === "string" &&
    typeof v.prompt === "string" &&
    typeof v.createdAt === "number"
  );
}

// Drops expired entries too, so a tab that never reads its prompt doesn't wedge the queue forever.
function readQueue(key: string): QueuedPrompt[] {
  const raw = window.localStorage.getItem(key);
  if (!raw) return [];
  try {
    const parsed: unknown = JSON.parse(raw);
    if (Array.isArray(parsed) && parsed.every(isQueuedPrompt)) {
      const cutoff = Date.now() - PROMPT_TTL_MS;
      return parsed.filter((p) => p.createdAt >= cutoff);
    }
  } catch {
    // Corrupt entry: drop it so it can't wedge future handoffs.
  }
  window.localStorage.removeItem(key);
  return [];
}

export interface ConsumedClarkPrompt {
  autoSubmit: boolean;
  prompt: string;
}

// Returns whether the handoff was actually stored. Callers that count the
// handoff need to tell a browser that refused the write apart from a prompt
// that was written and then lost on the way to the editor.
export function setPendingClarkPrompt(handoff: PendingClarkPrompt): boolean {
  try {
    const key = keyFor(handoff.applicationId);
    const entry: QueuedPrompt = {
      createdAt: Date.now(),
      nonce: handoff.nonce,
      prompt: handoff.prompt,
    };
    if (handoff.autoSubmit === true) {
      entry.autoSubmit = true;
    }
    const queue = [...readQueue(key), entry].slice(-MAX_QUEUE_LENGTH);
    window.localStorage.setItem(key, JSON.stringify(queue));
    return true;
  } catch {
    // Prefill is a nicety; if storage is unavailable the app still opens.
    return false;
  }
}

function consumeQueuedPrompt(match: QueuedPrompt): ConsumedClarkPrompt {
  // Anything other than an explicit `true` prefills rather than submits:
  // sending a turn spends tokens and edits the app, so a malformed entry
  // must not be read as consent to do that.
  return {
    autoSubmit: match.autoSubmit === true,
    prompt: match.prompt,
  };
}

function readMatchingPrompt(
  applicationId: string,
  nonce: string,
): { key: string; queue: QueuedPrompt[]; index: number } | null {
  const key = keyFor(applicationId);
  let queue: QueuedPrompt[];
  try {
    queue = readQueue(key);
  } catch {
    return null;
  }
  const index = queue.findIndex((p) => p.nonce === nonce);
  if (index === -1) return null;
  return { index, key, queue };
}

// Non-destructive so the editor can skip the finding-id fetch when this tab
// already has a localStorage handoff for `?clarkPrompt=`.
export function peekPendingClarkPrompt(
  applicationId: string,
  nonce: string,
): ConsumedClarkPrompt | null {
  const found = readMatchingPrompt(applicationId, nonce);
  if (!found) return null;
  return consumeQueuedPrompt(found.queue[found.index]);
}

// Matches by nonce (one per click), not FIFO, so sibling clicks for one applicationId never collide.
export function takePendingClarkPrompt(
  applicationId: string,
  nonce: string,
): ConsumedClarkPrompt | null {
  const found = readMatchingPrompt(applicationId, nonce);
  if (!found) return null;
  const [match] = found.queue.splice(found.index, 1);
  try {
    if (found.queue.length > 0) {
      window.localStorage.setItem(found.key, JSON.stringify(found.queue));
    } else {
      window.localStorage.removeItem(found.key);
    }
  } catch {
    // Ignore write failure; still return the prompt we already read.
  }
  return consumeQueuedPrompt(match);
}
